/**
 * Tree — the family, laid out by generation.
 *
 * `generation` is the only vertical fact the server sends: 0 is the root, its
 * parents are −1, its children +1. Everything horizontal is decided here.
 *
 * Two constraints shape the layout. Spouses must sit next to each other,
 * because a couple read apart is a couple somebody will mis-read. And children
 * should sit under their parents, so a row of eight cousins does not cross the
 * whole drawing. So each row is ordered by the average position of its parents
 * in the row above — a barycentre pass, one sweep, no iteration — and spouses
 * are then pulled adjacent.
 *
 * A disputed edge is drawn dashed and red. The archive holds a parish register
 * that disagrees with a family bible; the drawing has to be able to say that,
 * because rendering it as a plain line is the drawing telling a lie on the
 * family's behalf.
 */

import { h, keepScroll, replace, svg } from "../dom";
import { api, cropUrl } from "../api";
import { store } from "../state";
import { empty, failure, fingerprint, loading, seg } from "../ui";
import { lifespan, personName } from "../format";
import { closePersonGallery, openPersonGallery } from "../persongallery";
import type { Tree, TreeEdge, TreeNode } from "../types";
import type { RefreshOptions, View } from "./view";

const NW = 176; // node width
const NH = 62; // node height
const GX = 26; // gap between nodes on a row
const GY = 78; // gap between rows
const GUTTER = 92; // room on the left for the generation labels
const PAD = 24;

interface Placed {
  node: TreeNode;
  x: number;
  y: number;
}

const genName = (g: number): string => {
  if (g === 0) return "root";
  const up = ["", "parents", "grandparents", "great-grandparents"];
  const down = ["", "children", "grandchildren", "great-grandchildren"];
  const n = Math.abs(g);
  const words = g < 0 ? up : down;
  return words[n] ?? `${g < 0 ? "−" : "+"}${n} generations`;
};

/** Roughly how many characters of a name fit inside a node box. */
function fit(text: string, px: number, perChar: number): string {
  const max = Math.floor(px / perChar);
  return text.length <= max ? text : `${text.slice(0, Math.max(1, max - 1))}…`;
}

/**
 * Positions for every node. Exported without the DOM so the ordering rules can
 * be reasoned about on their own.
 */
export function layout(tree: Tree): { placed: Placed[]; width: number; height: number; rows: number[] } {
  const spouseOf = new Map<string, Set<string>>();
  const parentsOf = new Map<string, Set<string>>();
  for (const e of tree.edges) {
    if (e.kind === "spouse") {
      if (!spouseOf.has(e.from)) spouseOf.set(e.from, new Set());
      if (!spouseOf.has(e.to)) spouseOf.set(e.to, new Set());
      spouseOf.get(e.from)!.add(e.to);
      spouseOf.get(e.to)!.add(e.from);
    } else {
      if (!parentsOf.has(e.to)) parentsOf.set(e.to, new Set());
      parentsOf.get(e.to)!.add(e.from);
    }
  }

  const byGen = new Map<number, TreeNode[]>();
  for (const n of tree.nodes) {
    const at = byGen.get(n.generation);
    if (at) at.push(n);
    else byGen.set(n.generation, [n]);
  }
  const rows = [...byGen.keys()].sort((a, b) => a - b);

  const index = new Map<string, number>(); // id -> position in its own row
  const ordered = new Map<number, TreeNode[]>();

  for (const g of rows) {
    const row = byGen.get(g)!.slice();
    // Barycentre: where this person's parents sit on the row above. Someone
    // whose parents are not drawn keeps their birth order and goes last.
    const key = (n: TreeNode): number => {
      const ps = [...(parentsOf.get(n.identityId) ?? [])]
        .map((p) => index.get(p))
        .filter((v): v is number => v !== undefined);
      return ps.length ? ps.reduce((a, b) => a + b, 0) / ps.length : Number.POSITIVE_INFINITY;
    };
    row.sort((a, b) => {
      const d = key(a) - key(b);
      if (Number.isFinite(d) && d !== 0) return d;
      if (key(a) !== key(b)) return key(a) === Number.POSITIVE_INFINITY ? 1 : -1;
      return (a.birthYear ?? 9999) - (b.birthYear ?? 9999);
    });

    // Then pull spouses together: whenever somebody is placed, their partners
    // on this row follow immediately.
    const placedIds = new Set<string>();
    const final: TreeNode[] = [];
    for (const n of row) {
      if (placedIds.has(n.identityId)) continue;
      final.push(n);
      placedIds.add(n.identityId);
      for (const s of spouseOf.get(n.identityId) ?? []) {
        const partner = row.find((r) => r.identityId === s && !placedIds.has(s));
        if (partner) {
          final.push(partner);
          placedIds.add(partner.identityId);
        }
      }
    }
    ordered.set(g, final);
    final.forEach((n, i) => index.set(n.identityId, i));
  }

  const widest = Math.max(1, ...rows.map((g) => ordered.get(g)!.length));
  const canvas = widest * (NW + GX) - GX;
  const placed: Placed[] = [];
  rows.forEach((g, r) => {
    const row = ordered.get(g)!;
    const rowW = row.length * (NW + GX) - GX;
    const left = GUTTER + PAD + (canvas - rowW) / 2;
    row.forEach((n, i) => {
      placed.push({ node: n, x: left + i * (NW + GX), y: PAD + r * (NH + GY) });
    });
  });

  return {
    placed,
    width: GUTTER + PAD * 2 + canvas,
    height: PAD * 2 + rows.length * (NH + GY) - GY,
    rows,
  };
}

export function mount(pane: HTMLElement): View {
  let generation = 0;

  function draw(tree: Tree): HTMLElement {
    const { placed, width, height, rows } = layout(tree);
    const at = new Map(placed.map((p) => [p.node.identityId, p]));

    const root = svg("svg", {
      class: "tree",
      width: String(width),
      height: String(height),
      viewBox: `0 0 ${width} ${height}`,
    });

    // Generation labels down the left-hand gutter.
    rows.forEach((g, r) => {
      const y = PAD + r * (NH + GY) + NH / 2 + 4;
      root.appendChild(
        svg("text", { class: "genlbl", x: String(PAD), y: String(y) }, `${g > 0 ? "+" : g < 0 ? "−" : " "}${Math.abs(g) || ""} ${genName(g)}`.trim()),
      );
    });

    // Edges first, so nodes sit on top of them.
    const cls = (e: TreeEdge): string =>
      `edge ${e.kind}${e.certainty === "disputed" ? " disputed" : e.certainty === "probable" ? " probable" : ""}`;

    for (const e of tree.edges) {
      const a = at.get(e.from);
      const b = at.get(e.to);
      if (!a || !b) continue;

      if (e.kind === "spouse") {
        // Adjacent by construction in almost every case: a straight bar through
        // the gap. When the layout could not seat them together, arc over.
        const [l, r] = a.x <= b.x ? [a, b] : [b, a];
        const y = l.y + NH / 2;
        const gap = r.x - (l.x + NW);
        const d =
          Math.abs(l.y - r.y) < 1 && gap <= GX + 2
            ? `M${l.x + NW} ${y} H${r.x}`
            : `M${l.x + NW / 2} ${l.y} C${l.x + NW / 2} ${l.y - 34} ${r.x + NW / 2} ${r.y - 34} ${r.x + NW / 2} ${r.y}`;
        const path = svg("path", { class: cls(e), d });
        path.appendChild(
          svg("title", null, e.endedYear ? `married, ended ${e.endedYear}` : `married (${e.certainty})`),
        );
        root.appendChild(path);
      } else {
        // Parent above, child below: down, across the mid-line, down again.
        const px = a.x + NW / 2;
        const py = a.y + NH;
        const cx = b.x + NW / 2;
        const cy = b.y;
        const mid = py + (cy - py) / 2;
        const path = svg("path", {
          class: cls(e),
          d: `M${px} ${py} V${mid} H${cx} V${cy}`,
        });
        path.appendChild(
          svg(
            "title",
            null,
            `${personName(a.node.name)} → ${personName(b.node.name)} (${e.certainty})`,
          ),
        );
        root.appendChild(path);
      }
    }

    for (const p of placed) {
      const n = p.node;
      const isRoot = n.identityId === tree.rootId;
      const g = svg("g", {
        class: isRoot ? "node root" : "node",
        transform: `translate(${p.x},${p.y})`,
        tabindex: "0",
        role: "button",
        onclick: () => openPersonGallery(n, () => reroot(n.identityId)),
        onkeydown: (e: KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPersonGallery(n, () => reroot(n.identityId));
          }
        },
      });
      g.appendChild(
        svg("title", null, `${personName(n.name)} — ${n.kinship ?? "this is the root"}. Open their artifacts.`),
      );
      g.appendChild(svg("rect", { width: String(NW), height: String(NH), rx: "9" }));

      const crop = cropUrl(n.coverCrop);
      if (crop) {
        g.appendChild(
          svg("image", { href: crop, x: "8", y: "9", width: "44", height: "44", preserveAspectRatio: "xMidYMid slice" }),
        );
      }
      const tx = crop ? 60 : 12;
      const w = NW - tx - 10;
      g.appendChild(svg("text", { class: "nm", x: String(tx), y: "24" }, fit(personName(n.name), w, 7)));
      g.appendChild(
        svg("text", { class: "yr", x: String(tx), y: "39" }, fit(lifespan(n.birthYear, n.deathYear), w, 5.8)),
      );
      g.appendChild(
        svg(
          "text",
          { class: "kin", x: String(tx), y: "52" },
          fit(`${n.kinship ?? "root"} · ${n.photoCount} photo${n.photoCount === 1 ? "" : "s"}`, w, 5.8),
        ),
      );
      root.appendChild(g);
    }

    const wrap = h("div", { class: "treewrap" }, root);
    return wrap;
  }

  function reroot(id: string): void {
    store.go("tree", { treeRoot: id });
  }

  /** The tree last drawn, so a live update that changed nobody in it does nothing. */
  let shownSig: string | null = null;

  async function refresh(opts: RefreshOptions = {}): Promise<void> {
    generation++;
    const mine = generation;
    const quiet = Boolean(opts.quiet && shownSig !== null);
    if (!quiet) shownSig = null;

    const bar = h(
      "div",
      { class: "viewbar" },
      seg<"both" | "up" | "down">(
        "Direction",
        [
          ["both", "Both"],
          ["up", "Ancestors"],
          ["down", "Descendants"],
        ],
        store.treeDirection,
        (d) => {
          store.treeDirection = d;
          void refresh();
        },
      ),
      h(
        "div",
        { class: "segwrap" },
        h("span", { class: "lbl", text: "Depth" }),
        h(
          "div",
          { class: "field" },
          h(
            "select",
            {
              onchange: (e: Event) => {
                store.treeDepth = Number((e.target as HTMLSelectElement).value);
                void refresh();
              },
            },
            ...[1, 2, 3, 4, 5, 6, 8, 10].map((d) =>
              h("option", { value: String(d), selected: d === store.treeDepth, text: `${d} generations` }),
            ),
          ),
        ),
      ),
      h("button", {
        class: store.treePhotographed ? "chip on" : "chip",
        text: "Only the photographed",
        title: "Hide people the archive holds no photograph of",
        onclick: () => {
          store.treePhotographed = !store.treePhotographed;
          void refresh();
        },
      }),
    );

    if (!quiet) replace(pane, bar, loading("the family"));

    let tree: Tree;
    try {
      tree = await api.tree(store.libraryId, {
        root: store.route.treeRoot ?? undefined,
        depth: store.treeDepth,
        direction: store.treeDirection,
        photographed: store.treePhotographed || undefined,
      });
    } catch (e) {
      if (mine !== generation || quiet) return;
      replace(pane, bar, failure(e, () => void refresh()));
      return;
    }
    if (mine !== generation) return;
    const sig = fingerprint(tree);
    if (quiet && sig === shownSig) return;
    shownSig = sig;

    if (!tree.nodes.length) {
      replace(
        pane,
        bar,
        empty({
          icon: "tree",
          title: "Nobody is joined up yet",
          body: [
            "The tree is drawn from parent and spouse relationships. Record one — in the desktop app, or from a person's page — and everybody downstream of it is placed automatically, including the cousins nobody entered.",
          ],
          actions: [
            h("button", { class: "btn", text: "See the people", onclick: () => store.go("people", { personId: null }) }),
          ],
        }),
      );
      return;
    }

    const rootNode = tree.nodes.find((n) => n.identityId === tree.rootId);
    const foot = h(
      "div",
      { class: "treefoot" },
      h(
        "div",
        { class: "legend" },
        h("span", null, h("i", {}), "stated"),
        h("span", null, h("i", { class: "probable" }), "probable"),
        h("span", null, h("i", { class: "disputed" }), "disputed — the sources disagree"),
      ),
      h("span", {
        class: "stat",
        text: `${tree.nodes.length} placed · ${tree.edges.length} relationships${
          tree.unplaced ? ` · ${tree.unplaced} not connected to this family` : ""
        }`,
      }),
    );

    const head = h(
      "div",
      { class: "treehead" },
      h("h2", { text: rootNode ? `Around ${personName(rootNode.name)}` : "The family" }),
      h("p", {
        class: "sub",
        text: "Click anybody to see their photographs and details. Everything is measured from the person in the middle — the words under each name are their relationship to that root.",
      }),
      h("div", { class: "btnrow" },
        h("button", {
          class: "btn sm ghost",
          text: "Everyone",
          onclick: () => store.go("people", { personId: null }),
        }),
        rootNode
          ? h("button", {
              class: "btn sm",
              text: `Open ${personName(rootNode.name)}'s page`,
              onclick: () => store.go("people", { personId: rootNode.identityId }),
            })
          : null,
      ),
    );

    keepScroll(pane, () => replace(pane, bar, head, draw(tree), foot));
  }

  void refresh();

  return {
    refresh: (opts) => void refresh(opts),
    destroy() {
      generation++;
      closePersonGallery();
    },
  };
}
